/** Data-only ground-vehicle definitions. Runtime simulation/rendering live in
 * their respective packages so these records remain safe to validate, persist,
 * and use by a future authoritative server simulation. */

export type VehicleDriveKind = "hover" | "wheel" | "track";
export type VehicleSeatRole = "driver" | "gunner" | "passenger";
export type VehicleHardpointKind = "weapon" | "utility";
export type VehicleVec3 = readonly [number, number, number];

export interface VehicleProbeDef {
  /** Probe hard-point in chassis-local metres. */
  position: VehicleVec3;
  /** Desired ground clearance at this probe. */
  rideHeight: number;
  /** Extra reach below ride height before contact is lost. */
  travel: number;
  spring: number;
  damping: number;
}

export interface VehicleSeatDef {
  id: string;
  role: VehicleSeatRole;
  position: VehicleVec3;
  exit: VehicleVec3;
  hardpoints?: readonly string[];
  /** Optional rider IK sockets, relative to the avatar root placed at this
   * seat. Lets authored/procedural chassis line hands and feet up exactly. */
  riderPose?: VehicleRiderPoseDef;
  /** Optional authored root-warp profile for entering/leaving this seat.
   * `entry` is a chassis-local ground socket on the boarding side. */
  transition?: VehicleSeatTransitionDef;
}

export interface VehicleSeatTransitionDef {
  entry: VehicleVec3;
  mountDuration: number;
  dismountDuration: number;
  /** Legacy authoring value retained for definition compatibility. Approach is
   * now ordinary locomotion and is never consumed by the mount transition. */
  approachFraction: number;
  /** Local-up arc added while moving between the entry socket and seat. */
  arcHeight: number;
  /** Ground speed (m/s) at or above which leaving the seat becomes a BAIL: the
   * rider throws themselves clear instead of stepping down, and lands airborne
   * carrying the chassis' momentum. Omit for the speed-derived default. */
  bailSpeed?: number;
  /** Fallback bail length when no authored bail clip exists for the chassis. */
  bailDuration?: number;
  /** Push-off the rider gives themselves, in chassis-local m/s: `bailLift`
   * along local up, `bailPush` outward along the exit side. Small — the rest of
   * the trajectory is inherited chassis momentum plus gravity. */
  bailLift?: number;
  bailPush?: number;
}

export interface VehicleRiderPoseDef {
  bodyOffset: VehicleVec3;
  bodyPitch: number;
  leftHand: VehicleVec3;
  rightHand: VehicleVec3;
  leftFoot: VehicleVec3;
  rightFoot: VehicleVec3;
  /** Optional per-bone FK rotation offsets (euler XYZ radians) applied in the
   * bone's local space on top of the animation clip, keyed by rig bone name
   * (e.g. spine_03, neck_01, clavicle_l). Torso/clavicle bones are offset before
   * the arm/leg IK so the hands stay on the grips; wrists/ankles after. Authored
   * by the stance editor; omit for the default IK-only mount. */
  bones?: Readonly<Record<string, VehicleVec3>>;
  /** Optional elbow/knee pole-aim overrides (direction in chassis-local space).
   * Fall back to the built-in mount poles when omitted. */
  leftElbowPole?: VehicleVec3;
  rightElbowPole?: VehicleVec3;
  leftKneePole?: VehicleVec3;
  rightKneePole?: VehicleVec3;
}

export interface VehicleHardpointDef {
  /** Stable socket id referenced by seats, loadouts, and authored models. */
  id: string;
  kind: VehicleHardpointKind;
  /** Socket transform in chassis-local metres. Forward is local -Z. */
  position: VehicleVec3;
  direction: VehicleVec3;
  /** Input/fire group for weapon sockets. */
  group?: 1 | 2;
  /** Authored vehicle weapon installed in this socket. */
  weapon?: string;
}

export interface VehicleWeaponDef {
  id: string;
  name: string;
  damage: number;
  projectileSpeed: number;
  /** Per-mount cyclic rate. Twin mounts each obey this rate. */
  rpm: number;
}

export interface VehicleHandlingDef {
  maxSpeed: number;
  reverseSpeed: number;
  acceleration: number;
  reverseAcceleration: number;
  brakeAcceleration: number;
  coastDrag: number;
  lateralGrip: number;
  /** Handbrake drift tuning. Grip is a multiplier of normal lateral grip. */
  driftMinSpeed: number;
  driftGripMultiplier: number;
  driftYawMultiplier: number;
  driftDrag: number;
  /** How quickly released drift velocity realigns with the chassis nose. */
  driftRecovery: number;
  /** Maximum yaw rate at low and high speed, radians/second. */
  yawRateLow: number;
  yawRateHigh: number;
  yawResponse: number;
  uprightResponse: number;
  airControl: number;
  /** Radial acceleration while fully supported by the suspension. */
  gravity: number;
  /** Gravity scale at zero probe contact; blended down as support returns. */
  airborneGravityMultiplier: number;
  maxSlopeDeg: number;
}

export interface VehicleBoostDef {
  capacity: number;
  burnPerSecond: number;
  regenPerSecond: number;
  regenDelay: number;
  speedMultiplier: number;
  accelerationMultiplier: number;
  /** Overdrive snap-back: once boost ends, forward speed above the normal
   * maxSpeed is bled off at this rate (per second) even while the throttle is
   * held, so the surge reverts firmly instead of coasting on top speed. */
  overdriveDecay: number;
}

export interface VehicleColliderDef {
  /** Broad/sweep collision radius. Compound shapes can replace this later. */
  radius: number;
  halfHeight: number;
}

export interface VehicleHoverDef {
  /** Desired probe clearance with the hover system powered down. Zero lets
   * the hard chassis floor settle directly onto the terrain. */
  parkedRideHeight: number;
  /** Exponential response rates per second for powering up/down. */
  liftResponse: number;
  settleResponse: number;
}

/** Unpowered atmospheric-flight profile. Values are deliberately physical
 * quantities so differently sized ground vehicles can share the same flight
 * model without sharing the same performance. */
export interface VehicleGlideDef {
  /** Effective lifting surface in square metres. */
  wingArea: number;
  /** Local atmosphere density in kg/m^3. */
  airDensity: number;
  /** Linear pre-stall lift-curve slope, coefficient per radian. */
  liftSlope: number;
  zeroLiftAoADeg: number;
  stallAoADeg: number;
  baseDragCoefficient: number;
  /** Induced drag term in Cd = Cd0 + k Cl^2. */
  inducedDragFactor: number;
  sideDragCoefficient: number;
  minControlSpeed: number;
  maxBankDeg: number;
  /** Small launch impulse used to clear active suspension on detach. */
  detachImpulse: number;
}

/**
 * Powered atmospheric flight. Present on flyers (banshee/pelican-class craft);
 * absent on every ground vehicle. The block deliberately does NOT restate a
 * wing — the vehicle's ordinary {@link VehicleGlideDef} is the wing, and flight
 * is what turns the existing unpowered glide state into a sustained one:
 * thrust along the nose, hover authority at low airspeed, and a climb trim.
 *
 * The SOFT CEILING is not authored here. Every aerodynamic and thrust term is
 * multiplied by the local air-density fraction supplied by the ground adapter
 * (VehicleGround.airDensityAt — 1 at sea level, 0 at the top of a planet's
 * atmosphere, 0 everywhere on an airless world). Climb far enough and the
 * engine and the wing fade out together: the craft mushes over and sinks back,
 * with no wall and no clamp. Ships remain the only way out of an atmosphere.
 */
export interface VehicleFlightDef {
  /** Sustained thrust in newtons along the chassis nose while airborne. */
  thrust: number;
  /** Design cruise speed in m/s — the top of the speed range the CAMERA and
   * other presentation scale against. A flyer's actual top speed is emergent
   * from thrust vs drag, but its ground handling.maxSpeed is a taxi crawl, and
   * normalizing presentation by THAT pinned every speed-scaled effect (pullback,
   * FOV, environment warp) at maximum for the entire flight. */
  cruiseSpeed: number;
  /** Fraction of weight (0..1) the lift system can carry with no airspeed at
   * all — VTOL authority. A pelican hovers at ~1; a banshee wants ~0.7 so it
   * settles gently unless it keeps moving; 0 is a pure aeroplane. */
  vtolLift: number;
  /** Vertical trim acceleration (m/s^2) from the climb/sink inputs. */
  climbAuthority: number;
}

/**
 * The glide profile a vehicle gets when its authored source carries none — namely
 * asset-lab vehicles compiled via {@link compileVehicleDef} (AssetVehicleData has
 * no glide block yet). Sane, valid values (mirroring the scout's) so a compiled
 * vehicle passes vehicleDefinitionIssues and can fly. When glide authoring is added
 * to the asset format, the compiler should read it instead of falling back here.
 */
export const DEFAULT_VEHICLE_GLIDE: VehicleGlideDef = {
  wingArea: 0.5,
  airDensity: 1.225,
  liftSlope: 5.1,
  zeroLiftAoADeg: -9,
  stallAoADeg: 16,
  baseDragCoefficient: 0.048,
  inducedDragFactor: 0.075,
  sideDragCoefficient: 0.9,
  minControlSpeed: 16,
  maxBankDeg: 55,
  detachImpulse: 2.8,
};

export interface VehicleDef {
  id: string;
  name: string;
  drive: VehicleDriveKind;
  mass: number;
  inertia: VehicleVec3;
  probes: readonly VehicleProbeDef[];
  handling: VehicleHandlingDef;
  /** Occupancy-powered suspension profile. Omit for wheels/tracks or hover
   * vehicles whose ride height should remain active while empty. */
  hover?: VehicleHoverDef;
  /** Atmospheric glide capability. Present on every currently authored
   * vehicle, including wheel/track/hover ground chassis. */
  glide: VehicleGlideDef;
  boost?: VehicleBoostDef;
  /** Powered atmospheric flight. See {@link VehicleFlightDef} for why the
   * ceiling lives in the atmosphere rather than in this block. */
  flight?: VehicleFlightDef;
  collider: VehicleColliderDef;
  seats: readonly VehicleSeatDef[];
  hardpoints: readonly VehicleHardpointDef[];
  weapons: readonly VehicleWeaponDef[];
  camera: {
    distance: number;
    height: number;
    lookAhead: number;
    baseFov: number;
    speedFov: number;
  };
}

/** First production definition: deliberately asymmetric front/back probe
 * spacing so pitch response and long-chassis terrain following are exercised. */
export const SCOUT_SPEEDER: VehicleDef = {
  id: "scout-speeder",
  name: "SCOUT SPEEDER",
  drive: "hover",
  mass: 680,
  inertia: [920, 1280, 760],
  // Authored against the SHIPPED mesh (client/public/models/vehicles/scout-speeder.glb,
  // 4.12 m long x 1.04 tall once GlbVehicleView scales it): probes sit inboard of
  // the flanks rather than in mid-air, and the rear pair pulls in where the tail
  // narrows.
  //
  // The hull ran x +/-1.02 when these were measured. The turbines were enlarged
  // on 2026-07-29 and it now runs +/-1.15, so the probes sit further inboard on
  // the pods than originally drawn — still on solid geometry, which is all they
  // need. The 1.7 m collider radius still contains the hull. Re-measure off
  // build-scout-speeder.mts, which prints extents and landmarks every build.
  probes: [
    { position: [-0.85, -0.35, -1.9], rideHeight: 0.75, travel: 1.15, spring: 8800, damping: 2300 },
    { position: [0.85, -0.35, -1.9], rideHeight: 0.75, travel: 1.15, spring: 8800, damping: 2300 },
    { position: [-0.7, -0.35, 1.8], rideHeight: 0.7, travel: 1.05, spring: 8200, damping: 2200 },
    { position: [0.7, -0.35, 1.8], rideHeight: 0.7, travel: 1.05, spring: 8200, damping: 2200 },
  ],
  // The AGILE one: it turns inside anything else in the fleet and flies a little,
  // and pays for it in speed. Turn radius is roughly speed / yaw rate, so the
  // lower top speed and the higher yaw rates compound — the scout comes round in
  // well under half the space the bike needs. Lateral grip is raised to match, or
  // it would simply wash out instead of holding the tighter line.
  handling: {
    maxSpeed: 24,
    reverseSpeed: 18,
    acceleration: 11,
    reverseAcceleration: 6,
    brakeAcceleration: 78,
    coastDrag: 0.42,
    lateralGrip: 7.2,
    driftMinSpeed: 8,
    driftGripMultiplier: 0.06,
    driftYawMultiplier: 2.4,
    driftDrag: 0.12,
    driftRecovery: 10,
    yawRateLow: 3.2,
    // Deliberately high for a TOP-speed yaw rate. The rate interpolates on the
    // fraction of a vehicle's own top speed, so a slow vehicle sits at its HIGH
    // rate exactly where a fast one is still near its LOW rate — at 24 m/s the
    // scout is flat out while the bike is loafing at a third of its range. Left
    // at a conventional value the scout measured WORSE through 18-24 m/s than
    // the bike it is supposed to out-turn.
    yawRateHigh: 1.6,
    yawResponse: 10,
    uprightResponse: 7.5,
    airControl: 3.9,
    gravity: 14,
    airborneGravityMultiplier: 2.6,
    maxSlopeDeg: 58,
  },
  hover: {
    parkedRideHeight: 0,
    liftResponse: 3.8,
    settleResponse: 2.6,
  },
  glide: {
    // Modest but REAL lift now: detaching buys a shallow glide that carries the
    // scout across a gap, rather than the near-ballistic arc it used to fly.
    // Still nothing like a plane — the wing is small and it sinks steadily.
    wingArea: 5,
    airDensity: 1.225,
    liftSlope: 5.1,
    // Strongly cambered deployable surfaces: level chassis attitude is close
    // to the useful lift range, while pitching past 16 degrees still stalls.
    zeroLiftAoADeg: -9,
    stallAoADeg: 17,
    baseDragCoefficient: 0.044,
    inducedDragFactor: 0.068,
    sideDragCoefficient: 0.9,
    minControlSpeed: 14,
    maxBankDeg: 58,
    detachImpulse: 3,
  },
  boost: {
    capacity: 100,
    burnPerSecond: 24,
    regenPerSecond: 16,
    regenDelay: 0.8,
    speedMultiplier: 1.9,
    accelerationMultiplier: 2.0,
    overdriveDecay: 3.5,
  },
  collider: { radius: 1.7, halfHeight: 0.55 },
  seats: [
    {
      id: "driver",
      role: "driver",
      // The saddle is the DIP in the hull spine at z 0.73..0.98 (top y 0.15),
      // between the front cowl peaking at y 0.48 and the rear structure at 0.49.
      position: [0, 0.15, 0.85],
      exit: [-2.2, 0, 0.35],
      hardpoints: ["cannon-left", "cannon-right"],
      transition: {
        entry: [-2.15, 0, 0.85],
        mountDuration: 0.9,
        dismountDuration: 0.42,
        approachFraction: 0.38,
        // The hips translate onto the saddle; the outside-leg IK supplies
        // the visible Halo-style kick-over rather than hopping the whole body.
        arcHeight: 0.1,
        // A 24 m/s chassis: stepping off stops reading as a step somewhere
        // around a brisk run, which is where the bail takes over.
        bailSpeed: 9,
        bailDuration: 0.44,
        // Measured against the shipped hull, not guessed. The first pass (4.4 up,
        // 3.1 out) put the rider 1.4 m sideways at detach — barely past a 1.7 m
        // collider radius, so the leap read as hopping up off the saddle and
        // coming back down on it. It has to CLEAR the chassis to read as a bail.
        bailLift: 5,
        bailPush: 5.2,
      },
      // Authored in the STANCE editor (god panel → HARNESSES → STANCE). The
      // forward lean lives in the spine_01 bone offset + the hunch tuning below,
      // not bodyPitch; wrist offsets roll the hands onto the bars.
      riderPose: {
        bodyOffset: [0, -0.83, 0],
        bodyPitch: -0.16,
        // Hands on the front cowl (y 0.48 at z ~0.1). Boots STRADDLE THE BODY:
        // the mesh's spine was slimmed to a half-width of 0.33 at the foot
        // station (chassis z 0.50), so 0.38 out puts the inner edge of each boot
        // against the flank. They used to sit at 0.7, which was measured off the
        // old wide hull and left the legs splayed onto outboard pegs once the
        // body came in. Re-measure off build-scout-speeder.mts, which prints its
        // extents and landmarks on every build.
        leftHand: [-0.35, 0.33, -0.75],
        rightHand: [0.35, 0.33, -0.75],
        leftFoot: [-0.38, -0.35, -0.35],
        rightFoot: [0.38, -0.35, -0.35],
        leftElbowPole: [-0.74, -0.6, 0.22],
        rightElbowPole: [0.74, -0.6, 0.22],
        bones: {
          spine_01: [0.39, 0, -0.8],
          neck_01: [0.16, 0.01, 0.89],
          clavicle_r: [-0.48, 0, 0],
          hand_l: [-0.01, 0.28, -1.5],
          hand_r: [0.01, 0.83, -1.5],
        },
      },
    },
    { id: "passenger", role: "passenger", position: [0, 0.28, 1.55], exit: [2.2, 0, 1.4] },
  ],
  hardpoints: [
    { id: "cannon-left", kind: "weapon", position: [-0.75, -0.05, -1.95], direction: [0, 0, -1], group: 1, weapon: "pulse-cannon" },
    { id: "cannon-right", kind: "weapon", position: [0.75, -0.05, -1.95], direction: [0, 0, -1], group: 1, weapon: "pulse-cannon" },
  ],
  weapons: [
    { id: "pulse-cannon", name: "PULSE CANNON", damage: 14, projectileSpeed: 520, rpm: 300 },
  ],
  camera: { distance: 9.5, height: 3.6, lookAhead: 16, baseFov: 72, speedFov: 14 },
};

export const INTERCEPTOR_SPEEDER: VehicleDef = {
  ...SCOUT_SPEEDER,
  id: "interceptor-speeder",
  name: "INTERCEPTOR",
  mass: 430,
  inertia: [620, 850, 510],
  handling: {
    ...SCOUT_SPEEDER.handling,
    maxSpeed: 138,
    reverseSpeed: 30,
    acceleration: 46,
    brakeAcceleration: 60,
    lateralGrip: 6.8,
    yawRateLow: 2.9,
    yawRateHigh: 0.86,
  },
  boost: SCOUT_SPEEDER.boost ? {
    ...SCOUT_SPEEDER.boost,
    capacity: 75,
    burnPerSecond: 28,
    speedMultiplier: 1.85,
  } : undefined,
  glide: { ...SCOUT_SPEEDER.glide, wingArea: 8, baseDragCoefficient: 0.04, inducedDragFactor: 0.065, minControlSpeed: 20 },
  collider: { radius: 1.85, halfHeight: 0.68 },
  camera: { ...SCOUT_SPEEDER.camera, distance: 10.5, speedFov: 18 },
};

export const HAULER_SPEEDER: VehicleDef = {
  ...SCOUT_SPEEDER,
  id: "hauler-speeder",
  name: "HEAVY HAULER",
  mass: 1850,
  inertia: [2400, 3300, 2100],
  probes: SCOUT_SPEEDER.probes.map((p) => ({
    ...p,
    position: [p.position[0] * 1.35, p.position[1], p.position[2] * 1.4] as VehicleVec3,
    spring: p.spring * 2.2,
    damping: p.damping * 1.75,
  })),
  handling: {
    ...SCOUT_SPEEDER.handling,
    maxSpeed: 72,
    reverseSpeed: 18,
    acceleration: 20,
    brakeAcceleration: 38,
    lateralGrip: 4.7,
    yawRateLow: 1.65,
    yawRateHigh: 0.48,
    uprightResponse: 6.2,
  },
  boost: SCOUT_SPEEDER.boost ? {
    ...SCOUT_SPEEDER.boost,
    capacity: 140,
    burnPerSecond: 20,
    speedMultiplier: 1.6,
  } : undefined,
  glide: { ...SCOUT_SPEEDER.glide, wingArea: 25, baseDragCoefficient: 0.058, minControlSpeed: 22, maxBankDeg: 42, detachImpulse: 2.4 },
  collider: { radius: 2.9, halfHeight: 1.05 },
  camera: { ...SCOUT_SPEEDER.camera, distance: 13, height: 4.8 },
  seats: [
    ...SCOUT_SPEEDER.seats,
    { id: "rear-left", role: "passenger", position: [-0.8, 0.45, 1.8], exit: [-2.8, 0, 1.8] },
    { id: "rear-right", role: "passenger", position: [0.8, 0.45, 1.8], exit: [2.8, 0, 1.8] },
  ],
};

/** Authored-mesh chassis (client/public/models/vehicles/hover-bike.glb). Every
 * gameplay number below is authored against the SHIPPED model's dimensions
 * (3.2 m long, 0.9 m tall, 0.85 m wide once GlbVehicleView scales it), so the
 * probe rectangle, collider, and rider sockets sit on the visible hull. Same
 * hover/boost/glide feature set as the speeder — a light single-rider frame,
 * quicker to turn and faster flat out, with far less grip to lean on. */
export const HOVER_BIKE: VehicleDef = {
  id: "hover-bike",
  name: "HOVER BIKE",
  drive: "hover",
  mass: 340,
  inertia: [280, 420, 210],
  probes: [
    { position: [-0.34, -0.2, -1.2], rideHeight: 0.72, travel: 1.0, spring: 5200, damping: 1400 },
    { position: [0.34, -0.2, -1.2], rideHeight: 0.72, travel: 1.0, spring: 5200, damping: 1400 },
    { position: [-0.34, -0.2, 1.15], rideHeight: 0.68, travel: 0.95, spring: 4900, damping: 1350 },
    { position: [0.34, -0.2, 1.15], rideHeight: 0.68, travel: 0.95, spring: 4900, damping: 1350 },
  ],
  // The STRAIGHT-LINE one, and the exact inverse of the scout: it will out-run
  // anything on an open stretch and needs half a field to turn around. Slow yaw
  // rates plus a much higher top speed mean its turn radius is several times the
  // scout's — commit to a line early or overshoot.
  handling: {
    ...SCOUT_SPEEDER.handling,
    maxSpeed: 68,
    reverseSpeed: 16,
    acceleration: 32,
    reverseAcceleration: 8,
    brakeAcceleration: 66,
    coastDrag: 0.3,
    // A bike slides: less lateral grip than the speeder, and a drift that both
    // breaks away sooner and swings the nose harder once it does.
    lateralGrip: 4.2,
    driftMinSpeed: 6,
    driftYawMultiplier: 2.9,
    yawRateLow: 1.6,
    yawRateHigh: 0.55,
    yawResponse: 7.5,
    uprightResponse: 8.5,
    airControl: 3.4,
    // Leaves the ground like a thrown brick, which is the other half of "no glide".
    airborneGravityMultiplier: 3.1,
  },
  hover: { parkedRideHeight: 0, liftResponse: 4.4, settleResponse: 3 },
  // NO GLIDE. The definition still carries a valid profile (every vehicle must,
  // and the validator enforces positive values), but the numbers are chosen so
  // detaching gains nothing: a stub wing, heavy drag, a stall angle it exceeds
  // the moment the nose lifts, and a control speed it cannot reach in a dive.
  // Space becomes a hop, not a flight plan.
  glide: {
    ...SCOUT_SPEEDER.glide,
    wingArea: 0.1,
    liftSlope: 1,
    stallAoADeg: 6,
    baseDragCoefficient: 0.25,
    inducedDragFactor: 0.4,
    minControlSpeed: 80,
    maxBankDeg: 12,
    detachImpulse: 0.5,
  },
  boost: {
    capacity: 80,
    burnPerSecond: 26,
    regenPerSecond: 18,
    regenDelay: 0.7,
    speedMultiplier: 2.05,
    accelerationMultiplier: 2.2,
    overdriveDecay: 3.5,
  },
  collider: { radius: 1.35, halfHeight: 0.5 },
  seats: [
    {
      id: "driver",
      role: "driver",
      // Every socket below is measured off the SHIPPED mesh in chassis metres:
      // the saddle is the dip in the spine at z 0.0..0.4 (top y 0.26), the front
      // cowl the rider leans onto peaks at y 0.46 around z -0.7, and the hull
      // sides run out to x 0.43 — so hands ride the cowl and boots hang just
      // outside the flanks rather than sinking into the bodywork.
      position: [0, 0.26, 0.15],
      exit: [-1.9, 0, 0.2],
      hardpoints: ["repeater-left", "repeater-right"],
      transition: {
        entry: [-1.85, 0, 0.2],
        mountDuration: 0.9,
        dismountDuration: 0.42,
        approachFraction: 0.38,
        arcHeight: 0.1,
      },
      // Same authored-stance shape as the scout's saddle (see its comment), with
      // a deeper forward lean for the bike's longer reach to the cowl.
      riderPose: {
        bodyOffset: [0, -0.86, 0],
        bodyPitch: -0.2,
        leftHand: [-0.28, 0.2, -0.86],
        rightHand: [0.28, 0.2, -0.86],
        leftFoot: [-0.42, -0.5, -0.1],
        rightFoot: [0.42, -0.5, -0.1],
        leftElbowPole: [-0.74, -0.6, 0.22],
        rightElbowPole: [0.74, -0.6, 0.22],
        bones: {
          spine_01: [0.44, 0, -0.8],
          neck_01: [0.18, 0.01, 0.89],
          clavicle_r: [-0.48, 0, 0],
          hand_l: [-0.01, 0.28, -1.5],
          hand_r: [0.01, 0.83, -1.5],
        },
      },
    },
    { id: "pillion", role: "passenger", position: [0, 0.42, 0.95], exit: [1.9, 0, 0.95] },
  ],
  hardpoints: [
    { id: "repeater-left", kind: "weapon", position: [-0.3, 0.05, -1.32], direction: [0, 0, -1], group: 1, weapon: "pulse-repeater" },
    { id: "repeater-right", kind: "weapon", position: [0.3, 0.05, -1.32], direction: [0, 0, -1], group: 1, weapon: "pulse-repeater" },
  ],
  weapons: [
    { id: "pulse-repeater", name: "PULSE REPEATER", damage: 9, projectileSpeed: 560, rpm: 420 },
  ],
  camera: { distance: 8.2, height: 3.0, lookAhead: 16, baseFov: 72, speedFov: 16 },
};

/** First FLYER (authored-mesh chassis client/public/models/vehicles/m66-stingray.glb,
 * 7.1 m long x 6.2 m wide x 2.2 m tall at GLB_CHASSIS scale 3.7 — dimensions are
 * the reference sheet's, and the mesh builder hard-checks them). A light strike
 * craft: single PRONE pilot under the canopy, one big rear engine, two swept-
 * forward arms with tip cannons. On the ground it is a clumsy hover taxi; the
 * whole vehicle is the `flight` block, and its ceiling is the planet's own
 * atmosphere fading out from under it (see VehicleFlightDef).
 */
export const M66_STINGRAY: VehicleDef = {
  id: "m66-stingray",
  name: "M-66 STINGRAY",
  drive: "hover",
  mass: 860,
  inertia: [1650, 2400, 1500],
  // Footprint follows the airframe: the front pair sits under the swept-forward
  // arms, the rear pair under the engine mass. Long travel — a flyer lands on
  // its suspension out of a sink rate the speeders never see.
  probes: [
    { position: [-2.3, -0.4, -1.7], rideHeight: 0.95, travel: 1.3, spring: 11500, damping: 2900 },
    { position: [2.3, -0.4, -1.7], rideHeight: 0.95, travel: 1.3, spring: 11500, damping: 2900 },
    { position: [-0.8, -0.4, 2.4], rideHeight: 0.9, travel: 1.25, spring: 10800, damping: 2750 },
    { position: [0.8, -0.4, 2.4], rideHeight: 0.9, travel: 1.25, spring: 10800, damping: 2750 },
  ],
  // Ground handling is deliberately a TAXI: slow, wide, grippy enough to park.
  // The performance budget lives in the air.
  handling: {
    maxSpeed: 16,
    reverseSpeed: 10,
    acceleration: 9,
    reverseAcceleration: 6,
    brakeAcceleration: 40,
    coastDrag: 0.5,
    lateralGrip: 6.5,
    driftMinSpeed: 8,
    driftGripMultiplier: 0.12,
    driftYawMultiplier: 1.6,
    driftDrag: 0.14,
    driftRecovery: 9,
    yawRateLow: 1.9,
    yawRateHigh: 1.1,
    yawResponse: 8,
    uprightResponse: 7,
    // High air authority: this is the primary control surface for a flyer.
    airControl: 4.6,
    gravity: 14,
    // Near 1 — a flyer must not brick the instant its wing stalls; it falls at
    // authored gravity and recovers, unlike the deliberately brick-like bike.
    airborneGravityMultiplier: 1.15,
    maxSlopeDeg: 40,
  },
  hover: { parkedRideHeight: 0, liftResponse: 4.2, settleResponse: 2.6 },
  // A REAL wing at last — every ground vehicle's glide block is a compromise;
  // this one is the airframe. Big lifting body, gentle stall, slow minimum
  // control speed so the VTOL blend hands over smoothly.
  glide: {
    wingArea: 13,
    airDensity: 1.225,
    liftSlope: 5.1,
    zeroLiftAoADeg: -6,
    stallAoADeg: 18,
    baseDragCoefficient: 0.06,
    inducedDragFactor: 0.07,
    sideDragCoefficient: 1.1,
    minControlSpeed: 12,
    maxBankDeg: 65,
    detachImpulse: 3.5,
  },
  flight: {
    // 8600 N on 860 kg = 10 m/s^2 of nose authority at sea level; drag settles
    // the unboosted top speed around 110 m/s, inside the envelope the boosted
    // bike already exercises. Density scales it away with altitude.
    thrust: 8600,
    cruiseSpeed: 110,
    // Hangs on the fans at low speed but SETTLES — 0.85 of weight means a
    // stationary Stingray sinks at ~2 m/s^2 until the wing or the climb trim
    // picks it up. Hovering is work, not a parking brake.
    vtolLift: 0.85,
    climbAuthority: 10,
  },
  boost: {
    capacity: 90,
    burnPerSecond: 22,
    regenPerSecond: 14,
    regenDelay: 0.8,
    speedMultiplier: 1.5,
    accelerationMultiplier: 1.8,
    overdriveDecay: 3,
  },
  collider: { radius: 3.3, halfHeight: 0.8 },
  seats: [
    {
      id: "driver",
      role: "driver",
      // PRONE, per the reference sheet — the pilot lies along the spine under
      // the canopy. Authored by numbers + one probe iteration (the head landed
      // ~2 m aft of the seat with this rig's prone lean, so the seat sits well
      // FORWARD of where the head should end up — under the canopy crown).
      // Expect a stance-editor tuning session like every other chassis got.
      position: [0, 0.15, -1.15],
      exit: [-3.6, 0, 0],
      hardpoints: ["arm-cannon-left", "arm-cannon-right"],
      transition: {
        entry: [-3.5, 0, -0.4],
        mountDuration: 1.0,
        dismountDuration: 0.5,
        approachFraction: 0.38,
        arcHeight: 0.35,
        bailLift: 5,
        bailPush: 6,
      },
      riderPose: {
        // Sunk DEEP: the deck line sits ~0.9 m above the seat's floor, and only
        // what the canopy glass would show should breach it.
        bodyOffset: [0, -0.95, 0],
        bodyPitch: -0.78,
        leftHand: [-0.3, -0.15, -1.05],
        rightHand: [0.3, -0.15, -1.05],
        leftFoot: [-0.32, -0.3, 0.95],
        rightFoot: [0.32, -0.3, 0.95],
        leftElbowPole: [-0.7, -0.5, -0.3],
        rightElbowPole: [0.7, -0.5, -0.3],
        bones: {
          spine_01: [0.5, 0, 0],
          neck_01: [-0.55, 0, 0.85],
        },
      },
    },
  ],
  hardpoints: [
    { id: "arm-cannon-left", kind: "weapon", position: [-2.95, -0.15, -3.45], direction: [0, 0, -1], group: 1, weapon: "arm-cannon" },
    { id: "arm-cannon-right", kind: "weapon", position: [2.95, -0.15, -3.45], direction: [0, 0, -1], group: 1, weapon: "arm-cannon" },
  ],
  weapons: [
    { id: "arm-cannon", name: "ARM CANNON", damage: 16, projectileSpeed: 560, rpm: 340 },
  ],
  camera: { distance: 15, height: 5.2, lookAhead: 20, baseFov: 74, speedFov: 16 },
};

/** Second flyer (authored chassis client/public/models/vehicles/banshee.glb,
 * 5.6 m long x 5.9 m wide x 1.9 m tall at GLB_CHASSIS scale 3.0). The M-66's
 * little sibling and its handling opposite: a single-seat skirmisher — bulbous
 * cockpit pod, swept-BACK wings with downturned tip fins, twin nose repeaters.
 * Slower flat out than the Stingray, but it turns inside it everywhere, hangs
 * on its fans more confidently (vtolLift 0.9), and taxis like a gnat. */
export const BANSHEE: VehicleDef = {
  id: "banshee",
  name: "BANSHEE",
  drive: "hover",
  mass: 430,
  inertia: [520, 720, 470],
  probes: [
    { position: [-0.55, -0.35, -0.95], rideHeight: 0.8, travel: 1.1, spring: 6400, damping: 1650 },
    { position: [0.55, -0.35, -0.95], rideHeight: 0.8, travel: 1.1, spring: 6400, damping: 1650 },
    { position: [-1.5, -0.35, 0.65], rideHeight: 0.78, travel: 1.05, spring: 6000, damping: 1550 },
    { position: [1.5, -0.35, 0.65], rideHeight: 0.78, travel: 1.05, spring: 6000, damping: 1550 },
  ],
  handling: {
    maxSpeed: 14,
    reverseSpeed: 9,
    acceleration: 10,
    reverseAcceleration: 6,
    brakeAcceleration: 42,
    coastDrag: 0.5,
    lateralGrip: 6.8,
    driftMinSpeed: 7,
    driftGripMultiplier: 0.12,
    driftYawMultiplier: 1.6,
    driftDrag: 0.14,
    driftRecovery: 9,
    yawRateLow: 2.6,
    yawRateHigh: 1.5,
    yawResponse: 9,
    uprightResponse: 7.5,
    // The whole point of the airframe: it out-turns everything in the air.
    airControl: 5.4,
    gravity: 14,
    airborneGravityMultiplier: 1.1,
    maxSlopeDeg: 40,
  },
  hover: { parkedRideHeight: 0, liftResponse: 4.4, settleResponse: 2.8 },
  glide: {
    wingArea: 9,
    airDensity: 1.225,
    liftSlope: 5.1,
    zeroLiftAoADeg: -7,
    stallAoADeg: 19,
    baseDragCoefficient: 0.055,
    inducedDragFactor: 0.075,
    sideDragCoefficient: 1.2,
    minControlSpeed: 10,
    maxBankDeg: 75,
    detachImpulse: 3,
  },
  flight: {
    // 4700 N on 430 kg — nearly the Stingray's authority on half the mass, but
    // more drag per wing area settles it well short of the M-66's cruise.
    thrust: 4700,
    cruiseSpeed: 85,
    vtolLift: 0.9,
    climbAuthority: 11,
  },
  boost: {
    capacity: 70,
    burnPerSecond: 24,
    regenPerSecond: 15,
    regenDelay: 0.7,
    speedMultiplier: 1.5,
    accelerationMultiplier: 1.9,
    overdriveDecay: 3,
  },
  collider: { radius: 3.0, halfHeight: 0.65 },
  seats: [
    {
      id: "driver",
      role: "driver",
      // Seated and hunched under the canopy hump, bike-style — not prone.
      position: [0, 0.25, -0.2],
      exit: [-2.8, 0, 0],
      hardpoints: ["repeater-left", "repeater-right"],
      transition: {
        entry: [-2.7, 0, -0.1],
        mountDuration: 0.9,
        dismountDuration: 0.45,
        approachFraction: 0.38,
        arcHeight: 0.25,
        bailLift: 5,
        bailPush: 5.5,
      },
      riderPose: {
        bodyOffset: [0, -0.8, 0],
        bodyPitch: -0.3,
        leftHand: [-0.26, 0.1, -0.8],
        rightHand: [0.26, 0.1, -0.8],
        leftFoot: [-0.3, -0.38, 0.3],
        rightFoot: [0.3, -0.38, 0.3],
        leftElbowPole: [-0.7, -0.55, -0.1],
        rightElbowPole: [0.7, -0.55, -0.1],
        bones: {
          spine_01: [0.42, 0, -0.4],
          neck_01: [0.1, 0, 0.7],
        },
      },
    },
  ],
  hardpoints: [
    { id: "repeater-left", kind: "weapon", position: [-0.36, -0.42, -2.8], direction: [0, 0, -1], group: 1, weapon: "plasma-repeater" },
    { id: "repeater-right", kind: "weapon", position: [0.36, -0.42, -2.8], direction: [0, 0, -1], group: 1, weapon: "plasma-repeater" },
  ],
  weapons: [
    { id: "plasma-repeater", name: "PLASMA REPEATER", damage: 11, projectileSpeed: 460, rpm: 520 },
  ],
  camera: { distance: 11.5, height: 4.2, lookAhead: 18, baseFov: 74, speedFov: 15 },
};

export const VEHICLES: Readonly<Record<string, VehicleDef>> = {
  [SCOUT_SPEEDER.id]: SCOUT_SPEEDER,
  [HOVER_BIKE.id]: HOVER_BIKE,
  [INTERCEPTOR_SPEEDER.id]: INTERCEPTOR_SPEEDER,
  [HAULER_SPEEDER.id]: HAULER_SPEEDER,
  [M66_STINGRAY.id]: M66_STINGRAY,
  [BANSHEE.id]: BANSHEE,
};

export interface VehicleTuning {
  mass?: number;
  maxSpeed?: number;
  acceleration?: number;
  brakeAcceleration?: number;
  lateralGrip?: number;
  driftGripMultiplier?: number;
  driftYawMultiplier?: number;
  driftDrag?: number;
  driftRecovery?: number;
  yawRateLow?: number;
  yawRateHigh?: number;
  rideHeight?: number;
  parkedRideHeight?: number;
  hoverLiftResponse?: number;
  hoverSettleResponse?: number;
  springScale?: number;
  dampingScale?: number;
  boostCapacity?: number;
  boostSpeedMultiplier?: number;
  airborneGravityMultiplier?: number;
  glideWingArea?: number;
  glideAirDensity?: number;
  glideLiftSlope?: number;
  glideZeroLiftAoADeg?: number;
  glideStallAoADeg?: number;
  glideBaseDragCoefficient?: number;
  glideInducedDragFactor?: number;
  glideSideDragCoefficient?: number;
  glideMinControlSpeed?: number;
  glideMaxBankDeg?: number;
  glideDetachImpulse?: number;
}

function finiteRange(value: number | undefined, fallback: number, lo: number, hi: number): number {
  return value === undefined || !Number.isFinite(value) ? fallback : Math.max(lo, Math.min(hi, value));
}

/** Clone an authored definition with bounded runtime tuning. The id remains
 * authored so network peers can safely fall back to the base definition. */
export function tuneVehicle(base: VehicleDef, tuning: VehicleTuning): VehicleDef {
  const springScale = finiteRange(tuning.springScale, 1, 0.1, 8);
  const dampingScale = finiteRange(tuning.dampingScale, 1, 0.1, 8);
  return {
    ...base,
    mass: finiteRange(tuning.mass, base.mass, 100, 10_000),
    probes: base.probes.map((p) => ({
      ...p,
      rideHeight: finiteRange(tuning.rideHeight, p.rideHeight, 0.25, 6),
      spring: p.spring * springScale,
      damping: p.damping * dampingScale,
    })),
    handling: {
      ...base.handling,
      maxSpeed: finiteRange(tuning.maxSpeed, base.handling.maxSpeed, 5, 400),
      acceleration: finiteRange(tuning.acceleration, base.handling.acceleration, 1, 200),
      brakeAcceleration: finiteRange(tuning.brakeAcceleration, base.handling.brakeAcceleration, 1, 250),
      lateralGrip: finiteRange(tuning.lateralGrip, base.handling.lateralGrip, 0.1, 30),
      driftGripMultiplier: finiteRange(tuning.driftGripMultiplier, base.handling.driftGripMultiplier, 0.01, 1),
      driftYawMultiplier: finiteRange(tuning.driftYawMultiplier, base.handling.driftYawMultiplier, 1, 5),
      driftDrag: finiteRange(tuning.driftDrag, base.handling.driftDrag, 0.01, 5),
      driftRecovery: finiteRange(tuning.driftRecovery, base.handling.driftRecovery, 0, 30),
      yawRateLow: finiteRange(tuning.yawRateLow, base.handling.yawRateLow, 0.1, 8),
      yawRateHigh: finiteRange(tuning.yawRateHigh, base.handling.yawRateHigh, 0.05, 4),
      airborneGravityMultiplier: finiteRange(
        tuning.airborneGravityMultiplier,
        base.handling.airborneGravityMultiplier,
        1,
        8,
      ),
    },
    hover: base.hover ? {
      ...base.hover,
      parkedRideHeight: finiteRange(tuning.parkedRideHeight, base.hover.parkedRideHeight, 0, 3),
      liftResponse: finiteRange(tuning.hoverLiftResponse, base.hover.liftResponse, 0.25, 20),
      settleResponse: finiteRange(tuning.hoverSettleResponse, base.hover.settleResponse, 0.25, 20),
    } : undefined,
    glide: {
      ...base.glide,
      wingArea: finiteRange(tuning.glideWingArea, base.glide.wingArea, 0.1, 500),
      airDensity: finiteRange(tuning.glideAirDensity, base.glide.airDensity, 0.01, 5),
      liftSlope: finiteRange(tuning.glideLiftSlope, base.glide.liftSlope, 0.1, 12),
      zeroLiftAoADeg: finiteRange(tuning.glideZeroLiftAoADeg, base.glide.zeroLiftAoADeg, -30, 10),
      stallAoADeg: finiteRange(tuning.glideStallAoADeg, base.glide.stallAoADeg, 5, 45),
      baseDragCoefficient: finiteRange(
        tuning.glideBaseDragCoefficient,
        base.glide.baseDragCoefficient,
        0.001,
        2,
      ),
      inducedDragFactor: finiteRange(tuning.glideInducedDragFactor, base.glide.inducedDragFactor, 0.001, 2),
      sideDragCoefficient: finiteRange(tuning.glideSideDragCoefficient, base.glide.sideDragCoefficient, 0, 5),
      minControlSpeed: finiteRange(tuning.glideMinControlSpeed, base.glide.minControlSpeed, 1, 200),
      maxBankDeg: finiteRange(tuning.glideMaxBankDeg, base.glide.maxBankDeg, 1, 85),
      detachImpulse: finiteRange(tuning.glideDetachImpulse, base.glide.detachImpulse, 0, 50),
    },
    boost: base.boost ? {
      ...base.boost,
      capacity: finiteRange(tuning.boostCapacity, base.boost.capacity, 1, 1000),
      speedMultiplier: finiteRange(tuning.boostSpeedMultiplier, base.boost.speedMultiplier, 1, 5),
    } : undefined,
  };
}

export function vehicleDef(id: string): VehicleDef | null {
  return VEHICLES[id] ?? null;
}

/** Authored-data integrity checks used by tests and future content tooling. */
export function vehicleDefinitionIssues(def: VehicleDef): string[] {
  const issues: string[] = [];
  const seatIds = new Set<string>();
  const hardpointIds = new Set<string>();
  const weaponIds = new Set<string>();
  const glide = def.glide;
  if (!(glide.wingArea > 0 && glide.airDensity > 0 && glide.liftSlope > 0)) issues.push("invalid glide lift profile");
  if (!(glide.stallAoADeg > 0 && glide.stallAoADeg < 45 && glide.minControlSpeed > 0)) issues.push("invalid glide envelope");
  if (!(glide.baseDragCoefficient > 0 && glide.inducedDragFactor > 0 && glide.sideDragCoefficient >= 0)) {
    issues.push("invalid glide drag profile");
  }
  if (!(glide.maxBankDeg > 0 && glide.maxBankDeg < 90 && glide.detachImpulse >= 0)) issues.push("invalid glide controls");
  if (def.flight) {
    if (!(def.flight.thrust > 0)) issues.push("flight thrust must be positive");
    if (!(def.flight.cruiseSpeed > 0)) issues.push("flight cruiseSpeed must be positive");
    if (!(def.flight.vtolLift >= 0 && def.flight.vtolLift <= 1)) issues.push("flight vtolLift must be 0..1");
    if (!(def.flight.climbAuthority >= 0)) issues.push("flight climbAuthority must be non-negative");
    // A flyer's glide block is its wing; a stub wing (see the bike's) would
    // make the flight block a lie that only shows up in playtesting.
    if (def.glide.wingArea < 1) issues.push("flight requires a real wing (glide.wingArea >= 1)");
  }
  if (def.hover) {
    if (def.drive !== "hover") issues.push("hover power profile requires hover drive");
    if (!(Number.isFinite(def.hover.parkedRideHeight) && def.hover.parkedRideHeight >= 0)) {
      issues.push("invalid parked hover height");
    }
    if (!(def.hover.liftResponse > 0 && def.hover.settleResponse > 0)) issues.push("invalid hover response");
  }
  for (const weapon of def.weapons) {
    if (!weapon.id || weaponIds.has(weapon.id)) issues.push(`duplicate weapon: ${weapon.id}`);
    weaponIds.add(weapon.id);
    if (!(weapon.damage > 0 && weapon.projectileSpeed > 0 && weapon.rpm > 0)) issues.push(`invalid weapon stats: ${weapon.id}`);
  }
  for (const hp of def.hardpoints) {
    if (!hp.id || hardpointIds.has(hp.id)) issues.push(`duplicate hardpoint: ${hp.id}`);
    hardpointIds.add(hp.id);
    // Math.sqrt is exactly rounded (portable); Math.hypot is banned by pmath-ban.
    const length = Math.sqrt(hp.direction[0] * hp.direction[0] + hp.direction[1] * hp.direction[1] + hp.direction[2] * hp.direction[2]);
    if (!Number.isFinite(length) || length < 0.99 || length > 1.01) issues.push(`hardpoint direction must be normalized: ${hp.id}`);
    if (hp.kind === "weapon" && hp.group !== 1 && hp.group !== 2) issues.push(`weapon hardpoint requires a fire group: ${hp.id}`);
    if (hp.kind === "weapon" && (!hp.weapon || !weaponIds.has(hp.weapon))) issues.push(`weapon hardpoint requires a known weapon: ${hp.id}`);
  }
  let drivers = 0;
  for (const seat of def.seats) {
    if (!seat.id || seatIds.has(seat.id)) issues.push(`duplicate seat: ${seat.id}`);
    seatIds.add(seat.id);
    if (seat.role === "driver") drivers++;
    if (seat.transition && !(
      seat.transition.mountDuration > 0
      && seat.transition.dismountDuration > 0
      && seat.transition.approachFraction > 0
      && seat.transition.approachFraction < 1
      && seat.transition.arcHeight >= 0
    )) issues.push(`invalid seat transition: ${seat.id}`);
    // The bail profile is optional, but a present value must be usable: a zero
    // threshold would make every exit a bail, and a zero duration would detach
    // the rider on the frame they press the key.
    const bail = seat.transition;
    if (bail && [bail.bailSpeed, bail.bailDuration].some((v) => v !== undefined && !(v > 0))) {
      issues.push(`invalid seat bail profile: ${seat.id}`);
    }
    if (bail && [bail.bailLift, bail.bailPush].some((v) => v !== undefined && !(v >= 0))) {
      issues.push(`invalid seat bail impulse: ${seat.id}`);
    }
    for (const id of seat.hardpoints ?? []) {
      if (!hardpointIds.has(id)) issues.push(`seat ${seat.id} references unknown hardpoint: ${id}`);
    }
  }
  if (drivers !== 1) issues.push(`vehicle requires exactly one driver seat (found ${drivers})`);
  return issues;
}

export function vehicleWeapon(def: VehicleDef, id: string): VehicleWeaponDef | null {
  return def.weapons.find((weapon) => weapon.id === id) ?? null;
}

/** Chassis-local bail impulse: how hard the rider throws themselves clear. */
export interface VehicleBailImpulse {
  /** Along local up (m/s). */
  lift: number;
  /** Outward along the exit side (m/s). */
  push: number;
}

/**
 * Ground speed at which leaving the seat becomes a BAIL rather than a step-off.
 *
 * The default is deliberately RELATIVE to the chassis rather than one global
 * number: 24 m/s is flat out on the scout and a cruise on the hover bike, so an
 * absolute threshold would either make the scout un-bailable or make the bike
 * bail while it was still parking. The absolute floor only stops a hypothetical
 * crawler from treating walking pace as a bail.
 */
export function vehicleBailSpeed(def: VehicleDef, seat: VehicleSeatDef): number {
  const authored = seat.transition?.bailSpeed;
  if (authored !== undefined && authored > 0) return authored;
  return Math.max(7, def.handling.maxSpeed * 0.4);
}

export function vehicleBailImpulse(seat: VehicleSeatDef): VehicleBailImpulse {
  return {
    lift: seat.transition?.bailLift ?? 4.2,
    push: seat.transition?.bailPush ?? 2.8,
  };
}

export function speedSteerRate(def: VehicleHandlingDef, speed: number): number {
  const k = Math.max(0, Math.min(1, Math.abs(speed) / Math.max(1, def.maxSpeed)));
  const eased = k * k * (3 - 2 * k);
  return def.yawRateLow + (def.yawRateHigh - def.yawRateLow) * eased;
}

export function vehicleSubsteps(speed: number, radius: number, dt: number): number {
  // Never travel farther than half a hull radius in one contact update.
  return Math.max(1, Math.min(4, Math.ceil((Math.abs(speed) * dt) / Math.max(0.5, radius * 0.5))));
}
